import {
  trackMusicSeparationCompleted,
  trackMusicSeparationFailed,
  trackMusicSeparationStarted,
} from '@src/analytics/EventTracker';
import { useDatoClient, useDubbingStore } from '@src/stores-v2/StoreContext';
import { isAbortError } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import { reaction } from 'mobx';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { StemsSummary } from '@src/repositories/DubbingRepository';

const logger = LogManager.getLogger('feature:musicSeparation');

/**
 * Which splitter to use. `undefined` = the backend env default (server-chosen;
 * the frontend does not assume which provider). The explicit ids are the
 * launch-period override (DE4 / PLAT-10622).
 */
export type SplitterChoice = undefined | 'lalal-v1' | 'elevenlabs-isolation-v1';

export type SeparationStatus =
  | 'loading'
  | 'idle'
  | 'running'
  | 'done'
  | 'error';

/** The music axis under the voice control (PLAT-10626, DE5). */
export type MusicMode = 'none' | 'original';

/**
 * Outcome callbacks so the owner can raise a background toast (PLAT-10663) even
 * after the control popover has closed. They fire even when the host itself has
 * unmounted by then — the run lives in `DubbingStore`, not here, and a split the
 * person asked for must not finish silently because they closed the clip
 * preview or switched panels while it ran.
 */
export interface MusicSeparationCallbacks {
  onDone?: () => void;
  onError?: () => void;
}

export interface MusicSeparationController {
  /** `loading` while the initial state read is in flight. */
  status: SeparationStatus;
  /**
   * When the run in flight started (epoch ms), `null` when none is. Held here
   * rather than in the row because the row is unmounted whenever the language
   * menu is closed, while the job keeps running (PLAT-11563).
   */
  runStartedAt: number | null;
  /** A non-deleted stems variant exists (label Re-separate vs Separate). */
  hasStems: boolean;
  /** The kept non-voice bed is near-silent ("No music found"). */
  noMusic: boolean;
  /**
   * Signed URL of the kept music bed to play under the dub (PLAT-10626); `null`
   * until read, when there are no stems, or when the bed is silent.
   */
  bedUrl: string | null;
  /**
   * Per-format download URLs for the bed / voice stems (PLAT-10715): compact MP3
   * + lossless WAV; `null` when that format isn't available.
   */
  bedUrlMp3: string | null;
  bedUrlWav: string | null;
  voiceUrlMp3: string | null;
  voiceUrlWav: string | null;
  /** Signed URL of the un-separated source audio, for download (PLAT-10627); single native format; `null` if none. */
  originalUrl: string | null;
  /**
   * Provider that produced the current stems (`handler.provider`), for analytics
   * passthrough only (PLAT-10712); `null` until read. The frontend never branches
   * on it — the default splitter is server-chosen (provider-agnostic).
   */
  provider: string | null;
  /**
   * Byte size of each downloadable track, so the download menu can show the file
   * size next to its format (PLAT-10715). Absent until read / when unavailable.
   */
  sizes: {
    bedMp3?: number;
    bedWav?: number;
    voiceMp3?: number;
    voiceWav?: number;
    original?: number;
  };
  splitter: SplitterChoice;
  setSplitter: (choice: SplitterChoice) => void;
  /**
   * The None | Original music axis (ephemeral session state, mirroring the voice
   * axis; PLAT-10626 DS1). `original` plays the kept bed under a dub.
   */
  musicMode: MusicMode;
  setMusicMode: (mode: MusicMode) => void;
  /** Kick off a first-time separation. */
  separate: () => void;
  /** Re-run separation (force, ignoring the cache). */
  reseparate: () => void;
  /**
   * Stop waiting for the run in flight (PLAT-11563). The request is aborted and
   * the row returns to its idle state; the server may still finish the job, in
   * which case the stems show up on the next summary read.
   */
  cancel: () => void;
}

/**
 * Owns the state of the voice/music Separate action for a story (PLAT-10622).
 * Reads the current stems state on mount (Separate vs Re-separate), then runs
 * separation via the backend. Separation is language-agnostic (one decomposition
 * per video, reused across languages), so it keys on `storyId` only. Mirrors the
 * dub kick-off wiring (`useDubGenerateModalController` + `DubbingRepository`);
 * no business logic in the component.
 */
export function useMusicSeparationController(
  storyId: string,
  callbacks?: MusicSeparationCallbacks,
): MusicSeparationController {
  const datoClientStore = useDatoClient();
  const repository = datoClientStore.dubbingRepository;
  const dubbingStore = useDubbingStore();

  // Held in a ref so a fresh inline `callbacks` object each render doesn't
  // re-create `run` (or re-run the mount-read effect); the latest closures
  // (capturing the current `adapter`) are always used.
  const callbacksRef = useRef(callbacks);
  callbacksRef.current = callbacks;

  const [status, setStatus] = useState<SeparationStatus>('loading');
  const [hasStems, setHasStems] = useState(false);
  const [noMusic, setNoMusic] = useState(false);
  const [bedUrl, setBedUrl] = useState<string | null>(null);
  const [bedUrlMp3, setBedUrlMp3] = useState<string | null>(null);
  const [bedUrlWav, setBedUrlWav] = useState<string | null>(null);
  const [voiceUrlMp3, setVoiceUrlMp3] = useState<string | null>(null);
  const [voiceUrlWav, setVoiceUrlWav] = useState<string | null>(null);
  const [originalUrl, setOriginalUrl] = useState<string | null>(null);
  const [provider, setProvider] = useState<string | null>(null);
  const [sizes, setSizes] = useState<MusicSeparationController['sizes']>({});
  const [splitter, setSplitter] = useState<SplitterChoice>(undefined);
  // Ephemeral music axis (DS1): defaults to "From Original" so a separated story
  // plays its music under the dub by default. Not persisted — parity with the
  // voice axis, which is ephemeral too.
  const [musicMode, setMusicMode] = useState<MusicMode>('original');
  const [runStartedAt, setRunStartedAt] = useState<number | null>(null);

  const mountedRef = useRef(true);
  // True while the store's run is one THIS controller started — its own
  // `.then` settles the state, so the store-following effect below stands down.
  const ownsRunRef = useRef(false);
  // The story on screen. Transcript & Speakers stays mounted across a story
  // switch, so a run started for the previous story must not settle into this
  // one's state.
  const storyIdRef = useRef(storyId);
  if (storyIdRef.current !== storyId) {
    storyIdRef.current = storyId;
    ownsRunRef.current = false;
  }

  const applySummary = useCallback((summary: StemsSummary) => {
    setHasStems(summary.hasStems);
    setNoMusic(summary.noMusic);
    setBedUrl(summary.bedUrl ?? null);
    setBedUrlMp3(summary.bedUrlMp3 ?? null);
    setBedUrlWav(summary.bedUrlWav ?? null);
    setVoiceUrlMp3(summary.voiceUrlMp3 ?? null);
    setVoiceUrlWav(summary.voiceUrlWav ?? null);
    setOriginalUrl(summary.originalUrl ?? null);
    setProvider(summary.handler?.provider ?? null);
    setSizes({
      bedMp3: summary.bedBytesMp3,
      bedWav: summary.bedBytesWav,
      voiceMp3: summary.voiceBytesMp3,
      voiceWav: summary.voiceBytesWav,
      original: summary.originalBytes,
    });
    // A silent / absent bed can't play Original.
    if (!summary.hasStems || summary.noMusic) setMusicMode('none');
  }, []);

  // Initial state read: does a separation already exist?
  useEffect(() => {
    mountedRef.current = true;
    if (!storyId) {
      setStatus('idle');
      return;
    }
    const controller = new AbortController();
    void repository
      .getStemsSummary({ storyId, signal: controller.signal })
      .then(summary => {
        if (!mountedRef.current) return;
        applySummary(summary);
        // A run already in flight (picked up from the store) stays running.
        setStatus(current => (current === 'loading' ? 'idle' : current));
      })
      .catch(error => {
        if (isAbortError(error) || !mountedRef.current) return;
        setStatus(current => (current === 'loading' ? 'idle' : current)); // degrade to "offer Separate"
      });
    return () => {
      mountedRef.current = false;
      controller.abort();
    };
  }, [repository, storyId, applySummary]);

  // Follow the store's run, so a surface that mounts mid-split (the clip
  // preview reopened, another tab) shows it running instead of offering
  // Separate again, and every surface picks up the stems when it lands.
  useEffect(() => {
    if (!dubbingStore || !storyId) return;
    const followRun = reaction(
      () => dubbingStore.separationRun,
      run => {
        if (ownsRunRef.current) return;
        if (run?.storyId === storyId) {
          setStatus('running');
          setRunStartedAt(run.startedAt);
        } else {
          setStatus(current => (current === 'running' ? 'idle' : current));
          setRunStartedAt(null);
        }
      },
      { fireImmediately: true },
    );
    const followCompletion = reaction(
      () => dubbingStore.lastCompletedSeparation,
      run => {
        if (ownsRunRef.current || run?.storyId !== storyId) return;
        void repository
          .getStemsSummary({ storyId })
          .then(summary => {
            if (!mountedRef.current) return;
            applySummary(summary);
            if (summary.hasStems && !summary.noMusic) setMusicMode('original');
          })
          .catch(() => {
            /* advisory */
          });
      },
    );
    return () => {
      followRun();
      followCompletion();
    };
  }, [dubbingStore, repository, storyId, applySummary]);

  const run = useCallback(
    (regenerate: boolean) => {
      // Callbacks and analytics still report a run whose story is no longer
      // on screen; only the state it settles is gated on this.
      const isShown = () =>
        mountedRef.current && storyIdRef.current === storyId;
      ownsRunRef.current = true;
      setStatus('running');
      setRunStartedAt(Date.now());
      // Analytics report the provider as data only (never branched on); the
      // requested choice is the override or 'default' when server-chosen (DS3).
      const requestedProvider = splitter ?? 'default';
      trackMusicSeparationStarted({
        story_id: storyId,
        provider: requestedProvider,
        regenerate,
      });
      void dubbingStore
        .startSeparation({
          storyId,
          handlerInternalName: splitter,
          regenerate,
        })
        .then(result => {
          ownsRunRef.current = false;
          trackMusicSeparationCompleted({
            story_id: storyId,
            provider: result.handler.provider,
            variant_id: result.variantId,
            no_music: result.noMusic,
            cached: result.cached,
          });
          callbacksRef.current?.onDone?.();
          if (!isShown()) return;
          setHasStems(true);
          setNoMusic(result.noMusic);
          setStatus('done');
          setRunStartedAt(null);
          setProvider(result.handler.provider);
          if (result.noMusic) {
            setBedUrl(null);
            setBedUrlMp3(null);
            setBedUrlWav(null);
            setMusicMode('none');
          } else {
            // DS1: a just-separated story with music defaults to From Original
            // (parity with an already-separated story's mount-read default), so
            // the kept bed plays under the dub without a manual toggle.
            setMusicMode('original');
          }
          // The separate result carries no signed URLs; refresh them from the
          // summary (best-effort — playback/download just wait until they land).
          void repository
            .getStemsSummary({ storyId })
            .then(summary => {
              if (!isShown()) return;
              setBedUrl(summary.bedUrl ?? null);
              setBedUrlMp3(summary.bedUrlMp3 ?? null);
              setBedUrlWav(summary.bedUrlWav ?? null);
              setVoiceUrlMp3(summary.voiceUrlMp3 ?? null);
              setVoiceUrlWav(summary.voiceUrlWav ?? null);
              setOriginalUrl(summary.originalUrl ?? null);
              setSizes({
                bedMp3: summary.bedBytesMp3,
                bedWav: summary.bedBytesWav,
                voiceMp3: summary.voiceBytesMp3,
                voiceWav: summary.voiceBytesWav,
                original: summary.originalBytes,
              });
            })
            .catch(() => {
              /* advisory */
            });
        })
        .catch(error => {
          if (isAbortError(error)) {
            // Stopped from the on-page chip (or another surface): back to
            // idle — unless a newer run replaced this one.
            if (dubbingStore.separationRun) return;
            ownsRunRef.current = false;
            if (!isShown()) return;
            setStatus('idle');
            setRunStartedAt(null);
            return;
          }
          ownsRunRef.current = false;
          logger.error('separateStems failed', { storyId });
          trackMusicSeparationFailed({
            story_id: storyId,
            provider: requestedProvider,
            error_message:
              error instanceof Error ? error.message : String(error),
          });
          callbacksRef.current?.onError?.();
          if (!isShown()) return;
          setStatus('error');
          setRunStartedAt(null);
        });
    },
    [dubbingStore, repository, storyId, splitter],
  );

  const separate = useCallback(() => run(false), [run]);
  const reseparate = useCallback(() => run(true), [run]);
  const cancel = useCallback(() => {
    if (dubbingStore.separationRun?.storyId !== storyId) return;
    dubbingStore.cancelSeparation();
    logger.info('separateStems cancelled by the user', { storyId });
    setStatus('idle');
    setRunStartedAt(null);
  }, [dubbingStore, storyId]);

  return {
    status,
    runStartedAt,
    hasStems,
    noMusic,
    bedUrl,
    bedUrlMp3,
    bedUrlWav,
    voiceUrlMp3,
    voiceUrlWav,
    originalUrl,
    provider,
    sizes,
    splitter,
    setSplitter,
    musicMode,
    setMusicMode,
    separate,
    reseparate,
    cancel,
  };
}
